import { fetchClient } from "@/api/api";
import type {
	BatchUpdateTransferPlayers,
	GeneralApiError,
	createSnipeTransferPlayersInBatchInput,
	createSnipeTransferPlayersInBatchOutput,
	deletePlayerInput,
	deletePlayerOutput,
	deleteSnipeTransferPlayersInBatchInput,
	deleteSnipeTransferPlayersInBatchOutput,
	getPlayerDatabaseFacetsOutput,
	getPlayerDatabaseInput,
	getPlayerDatabaseOutput,
	getPlayersInput,
	getPlayersOutput,
	getSnipeTransferPlayerSuggestionsFacetsOutput,
	getSnipeTransferPlayerSuggestionsInput,
	getSnipeTransferPlayerSuggestionsOutput,
	getTransferPlayersFacetsOutput,
	readAllPlayersInput,
	readAllPlayersOutput,
	refreshPlayerPricesInput,
	refreshPlayerPricesOutput,
	resetSnipeTransferPlayersSuccessRatioInput,
	updatePlayerInput,
	updatePlayerOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { createFacetSource } from "@/services/facets";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TRANSFER_PLAYERS_SERVICE_KEYS } from "./keys";

/** The player database can hide transfer players, so it changes whenever they do. */
const invalidatePlayerDatabase = () => {
	queryClient.invalidateQueries({
		queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase(),
	});
	queryClient.invalidateQueries({
		queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetPlayerDatabaseFacets(),
	});
};

const useGetTransferPlayers: CustomQueryHook<
	readAllPlayersOutput,
	readAllPlayersInput
> = (payload, options) => {
	return useQuery({
		queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/snipe-transfer-players/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useGetTransferPlayerSuggestions: CustomQueryHook<
	getSnipeTransferPlayerSuggestionsOutput,
	getSnipeTransferPlayerSuggestionsInput
> = (payload, options) => {
	return useQuery({
		queryKey:
			TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestions(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/snipe-transfer-player-suggestions/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useGetPlayerDatabase: CustomQueryHook<
	getPlayerDatabaseOutput,
	getPlayerDatabaseInput
> = (payload, options) => {
	return useQuery({
		queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/player-database/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useSearchPlayers: CustomQueryHook<getPlayersOutput, getPlayersInput> = (
	payload,
	options,
) => {
	return useQuery({
		queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useSearchPlayers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/players/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useCreateTransferPlayers: CustomMutationHook<
	createSnipeTransferPlayersInBatchInput,
	createSnipeTransferPlayersInBatchOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: TRANSFER_PLAYERS_SERVICE_KEYS.useCreateTransferPlayers(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/snipe-transfer-players/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.transferPlayers.toast.createSuccess"));
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestions(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestionsFacets(),
			});
			invalidatePlayerDatabase();
		},
	});
};

const useUpdateTransferPlayer: CustomMutationHook<
	updatePlayerInput,
	updatePlayerOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: TRANSFER_PLAYERS_SERVICE_KEYS.useUpdateTransferPlayer(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/snipe-transfer-players/{transfer_player_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: (_, payload) => {
			const { activation_state } = payload.body;
			toast.success(
				activation_state === undefined || activation_state === null
					? t("admin.transferPlayers.toast.updateSuccess")
					: activation_state === "ACTIVE"
						? t("admin.transferPlayers.toast.activateSuccess")
						: t("admin.transferPlayers.toast.deactivateSuccess"),
			);
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(),
			});
		},
	});
};

const useDeleteTransferPlayer: CustomMutationHook<
	deletePlayerInput,
	deletePlayerOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: TRANSFER_PLAYERS_SERVICE_KEYS.useDeleteTransferPlayer(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/marketplace/snipe-transfer-players/{transfer_player_id}",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.transferPlayers.toast.deleteSuccess"));
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestions(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestionsFacets(),
			});
			invalidatePlayerDatabase();
		},
	});
};

const useDeleteTransferPlayersInBatch: CustomMutationHook<
	deleteSnipeTransferPlayersInBatchInput,
	deleteSnipeTransferPlayersInBatchOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey:
			TRANSFER_PLAYERS_SERVICE_KEYS.useDeleteTransferPlayersInBatch(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/snipe-transfer-players/delete-many/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: (_, payload) => {
			toast.success(
				t("admin.transferPlayers.toast.deleteSelectedSuccess", {
					value: payload.body.ids.length,
				}),
			);
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestions(),
			});
			queryClient.invalidateQueries({
				queryKey:
					TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestionsFacets(),
			});
			invalidatePlayerDatabase();
		},
	});
};

const useBatchUpdateTransferPlayers = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, GeneralApiError, BatchUpdateTransferPlayers>({
		mutationKey: TRANSFER_PLAYERS_SERVICE_KEYS.useBatchUpdateTransferPlayers(),
		mutationFn: async (body) => {
			const { error } = await fetchClient.POST(
				"/v1/marketplace/admin/snipe-transfer-players/update-many/",
				{ body, throwOnError: true },
			);
			if (error) throw error;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(),
			});
		},
	});
};

const useResetTransferPlayersSuccessRatio: CustomMutationHook<
	resetSnipeTransferPlayersSuccessRatioInput,
	unknown
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey:
			TRANSFER_PLAYERS_SERVICE_KEYS.useResetTransferPlayersSuccessRatio(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/snipe-transfer-players/reset-success-ratio/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: (_, payload) => {
			toast.success(
				t("admin.transferPlayers.toast.resetSuccessRatioSuccess", {
					value: payload.body.ids.length,
				}),
			);
			queryClient.invalidateQueries({
				queryKey: TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
			});
		},
	});
};

/** Fetches the prices from fut.gg right now; every player list shows the new ones. */
const useRefreshPlayerPrices: CustomMutationHook<
	refreshPlayerPricesInput,
	refreshPlayerPricesOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: TRANSFER_PLAYERS_SERVICE_KEYS.useRefreshPlayerPrices(),
		mutationFn: async (payload) => {
			// the backend takes up to 100 ids per call
			const ids = [...new Set(payload.body.resource_ids)];
			const refreshed: refreshPlayerPricesOutput = [];
			for (let i = 0; i < ids.length; i += 100) {
				const { data, error } = await fetchClient.POST(
					"/v1/marketplace/admin/players/refresh-prices/",
					{ body: { resource_ids: ids.slice(i, i + 100) }, throwOnError: true },
				);
				if (error) throw error;
				refreshed.push(...data);
			}
			return refreshed;
		},
		onError: toastApiError,
		onSuccess: (data, payload) => {
			const requested = new Set(payload.body.resource_ids).size;
			if (data.length === requested) {
				toast.success(
					t("admin.transferPlayers.toast.refreshPriceSuccess", {
						value: data.length,
					}),
				);
			} else {
				toast.warning(
					t("admin.transferPlayers.toast.refreshPricePartial", {
						value: data.length,
						total: requested,
					}),
				);
			}
		},
		// a later batch can fail after earlier ones saved prices, so refresh either way
		onSettled: () => {
			for (const queryKey of [
				TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayers(),
				TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestions(),
				TRANSFER_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase(),
			]) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

const TRANSFER_PLAYERS_FACET_SOURCE =
	createFacetSource<getTransferPlayersFacetsOutput>({
		path: "/v1/marketplace/snipe-transfer-players/facets/",
		queryKey: (payload) =>
			TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayersFacets(payload as any),
	});

const TRANSFER_PLAYER_SUGGESTIONS_FACET_SOURCE =
	createFacetSource<getSnipeTransferPlayerSuggestionsFacetsOutput>({
		path: "/v1/marketplace/snipe-transfer-player-suggestions/facets/",
		queryKey: (payload) =>
			TRANSFER_PLAYERS_SERVICE_KEYS.useGetTransferPlayerSuggestionsFacets(
				payload as any,
			),
	});

const PLAYER_DATABASE_FACET_SOURCE =
	createFacetSource<getPlayerDatabaseFacetsOutput>({
		path: "/v1/marketplace/admin/player-database/facets/",
		queryKey: (payload) =>
			TRANSFER_PLAYERS_SERVICE_KEYS.useGetPlayerDatabaseFacets(payload as any),
	});

export {
	PLAYER_DATABASE_FACET_SOURCE,
	TRANSFER_PLAYERS_FACET_SOURCE,
	TRANSFER_PLAYER_SUGGESTIONS_FACET_SOURCE,
	useGetTransferPlayers,
	useGetTransferPlayerSuggestions,
	useGetPlayerDatabase,
	useRefreshPlayerPrices,
	useSearchPlayers,
	useCreateTransferPlayers,
	useUpdateTransferPlayer,
	useDeleteTransferPlayer,
	useDeleteTransferPlayersInBatch,
	useBatchUpdateTransferPlayers,
	useResetTransferPlayersSuccessRatio,
};
